import React, { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import loadable from "@loadable/component";
const ImageLoader = loadable(() => import("../common/ImageLoader"));

gsap.registerPlugin(ScrollTrigger);
import screenImgOne from "assets/images/performanceRefresh/filterGraph/caught-me-cleaning.png";
import screenImgOneWebp from "assets/images/performanceRefresh/filterGraph/caught-me-cleaning.png?as=webp";
import screenImgTwo from "assets/images/performanceRefresh/filterGraph/pretend-im-not-even-here.png";
import screenImgTwoWebp from "assets/images/performanceRefresh/filterGraph/pretend-im-not-even-here.png?as=webp";

import statsImgLargeOne from "assets/images/performanceRefresh/filterGraph/air-changes-graph-lg.svg";
import statsImgSmallOne from "assets/images/performanceRefresh/filterGraph/air-changes-graph-sm.svg";
import statsImgLargeTwo from "assets/images/performanceRefresh/filterGraph/decibels-graph-lg.svg";
import statsImgSmallTwo from "assets/images/performanceRefresh/filterGraph/decibels-graph-sm.svg";

const cardData = [        
  {
    id: 1,
    image: screenImgOne,
    imageWebp: screenImgOneWebp,
    title: "Mila deep cleans when nobody is listening",
    description: "Mila delivers 40% more effective air changes per day by adjusting power based on room occupancy, ramping up when you're away for a deeper clean.",
    statsTitle: "Mila delivers 40% more effective cleaning power per day",
    statsImage: statsImgLargeOne,
    statsImageSmall: statsImgSmallOne,
    alt: "Mila caught me cleaning"
  },
  {
    id: 2,
    image: screenImgTwo,
    imageWebp: screenImgTwoWebp,
    title: "How does she do that...",
    description: "Mila reduces your daily noise exposure by 60% based on room occupancy, ensuring a quieter environment when you're around.",
    statsTitle: "And reduces your noise exposure by 60% per day",
    statsImage: statsImgLargeTwo,
    statsImageSmall: statsImgSmallTwo,
    alt: "Mila caught me cleaning"
  }
];

/**
 * @returns {JSX.Element}
 * @constructor
 */

const FilterGraph = () => {
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const pinnedCardRef = useRef(null);
  const activeIndexRef = useRef(0);

  // Check if mobile on mount and window resize
  useEffect(() => {
    let timeoutId;

    const checkMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newIsMobile = window.innerWidth < 769;
        setIsMobile(newIsMobile);
      }, 150); // Debounce resize events
    };

    // Initial check
    setIsMobile(window.innerWidth < 769);

    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || !pinnedCardRef.current) return;

    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.trigger === containerRef.current) {
        trigger.kill();
      }
    });

    if (isMobile) {
      setActiveCardIndex(0);
      return;
    }

    // Desktop: No ScrollTrigger animation, just stack items
    // Remove any existing ScrollTrigger setup
  }, [isMobile]);

  return (
    <section className="p-filter-graph">
      <div className="container">
        <div className="p-filter-graph__header">
          <h2 className="p-filter-graph__title">
            And then there's her <br />
            patent-pending secret weapon ...
          </h2>
          <div className="p-filter-graph__highlight">
            <p>
              Mila is the first purifier to detect if someone is nearby, so she
              can deep clean when you aren't around.{" "}
            </p>
          </div>
          <div className="p-filter-graph__description">
            <p>
              How come no one thought of this before? Other air purifiers only
              spin up to max performance when there's a problem in the air, and
              they get super loud. Mila is smart enough to go all out,
              preemptively (and quietly), delivering more power with less noise.
            </p>
          </div>
        </div>

        <div className="p-filter-graph__pinned-container" ref={containerRef}>
          <div className="p-filter-graph__pinned-card" ref={pinnedCardRef}>
            {/* On both mobile and desktop, show all cards stacked */}
            {cardData.map((card, index) => (
              <StickyCardContent key={card.id} cardData={card} />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

const StickyCardContent = ({ cardData }) => {
  const imageRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const statsTitleRef = useRef(null);
  const statsImageRef = useRef(null);

  useEffect(() => {
    if (!imageRef.current) return;

    // Remove animations - just show content without animations on both mobile and desktop
    gsap.set([
      imageRef.current,
      titleRef.current,
      descriptionRef.current,
      statsTitleRef.current,
      statsImageRef.current
    ], {
      opacity: 1,
      y: 0
    });
  }, [cardData]);

  if (!cardData) return null;

  return (
    <div className="p-filter-graph__card">
      <div className="p-filter-graph__grid">
        <div className="p-filter-graph__grid-item p-graph-item">
          <ImageLoader
            ref={imageRef}
            className="p-graph-item__image img-relative"
            placeholderAspectRatio={50 / 43}
            image={cardData.image}
            webp={cardData.imageWebp}
            alt={cardData.alt}
          />
          <div className="p-graph-item__content">
            <h3 ref={titleRef} className="p-graph-item__title">
              {cardData.title}
            </h3>
            <p
              ref={descriptionRef}
              className="p-graph-item__description"
              style={{ maxWidth: "297px" }}
            >
              {cardData.description}
            </p>
          </div>
        </div>
        <div className="p-filter-graph__grid-item p-graph-stats">
          <h2 ref={statsTitleRef} className="p-graph-stats__title">
            {cardData.statsTitle}
          </h2>
          <ImageLoader
            ref={statsImageRef}
            className="p-graph-stats__image p-graph-stats__image--sm img-relative"
            placeholderAspectRatio={41 / 20}
            svgUrl={cardData.statsImage}
            svgSmUrl={cardData.statsImageSmall}
            alt={cardData.statsTitle}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterGraph;
